import type { SnapEndpointDiscovery, SnapError } from '../types';
import { SnapDenyCodes } from '../types';
import { createSnapError } from '../security/sanitize';
import { CustomerMessages, mapCustomerError } from '../security/customerErrors';

export interface MyRequestListItem {
  ticketId: number | string;
  ticketNumber?: string;
  title?: string;
  status?: string;
  priority?: number | string;
  categoryName?: string;
  createdOn?: string;
  closedOn?: string;
  /** Server list VM has no UpdatedOn; prefer closedOn else createdOn for display. */
  updatedOn?: string;
}

export interface MyRequestDetail {
  ticketId: number | string;
  ticketNumber?: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: number | string;
  categoryName?: string;
  createdOn?: string;
  closedOn?: string;
  comments?: Array<{
    commentId?: number | string;
    comment?: string;
    createdOn?: string;
    author?: string;
  }>;
  attachments?: Array<{
    id?: number | string;
    fileName?: string;
    contentType?: string;
    length?: number;
    evidenceKind?: string;
    sourceProducer?: string;
    uploadedOn?: string;
  }>;
  activity?: Array<{
    summary?: string;
    createdOn?: string;
    actor?: string;
  }>;
}

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

function replaceTicketPath(template: string, ticketId: number | string): string {
  return template
    .replace('{id}', encodeURIComponent(String(ticketId)))
    .replace('{ticketId}', encodeURIComponent(String(ticketId)));
}

function authHeaders(
  endpoints: SnapEndpointDiscovery,
  authorization: string,
  correlationId: string
): Record<string, string> {
  return {
    Accept: 'application/json',
    [endpoints.correlationHeaderName]: correlationId,
    [endpoints.bootstrapHeaderName]: authorization
  };
}

function mapHttpError(
  body: Record<string, unknown>,
  status: number,
  correlationId: string,
  unavailableMessage: string
): SnapError {
  const code = String(body.code ?? body.Code ?? '');
  const message = String(body.message ?? body.Message ?? '');
  if (/please log in/i.test(message) || status === 401 || status === 403) {
    return mapCustomerError({ code, message, httpStatus: status, correlationId });
  }
  return createSnapError(
    'transport_failed',
    code || SnapDenyCodes.TransportFailed,
    unavailableMessage,
    { correlationId, retryable: status >= 500, userSafe: true }
  );
}

function pickDate(...values: unknown[]): string | undefined {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v;
  }
  return undefined;
}

export async function listMyRequests(args: {
  apiBaseUrl: string;
  endpoints: SnapEndpointDiscovery;
  authorization: string;
  correlationId: string;
  fetchImpl: typeof fetch;
  signal?: AbortSignal;
}): Promise<{ ok: true; items: MyRequestListItem[] } | { ok: false; error: SnapError }> {
  const path = args.endpoints.myRequestsPath || '/api/v1/SupportTickets/MyTickets';
  const url = joinUrl(args.apiBaseUrl, path);
  try {
    const response = await args.fetchImpl(url, {
      method: 'GET',
      headers: authHeaders(args.endpoints, args.authorization, args.correlationId),
      credentials: 'omit',
      signal: args.signal
    });
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    if (!response.ok) {
      return {
        ok: false,
        error: mapHttpError(
          (body as Record<string, unknown>) || {},
          response.status,
          args.correlationId,
          CustomerMessages.myRequestsUnavailable
        )
      };
    }
    const rows = Array.isArray(body)
      ? body
      : Array.isArray((body as { items?: unknown })?.items)
        ? (body as { items: unknown[] }).items
        : [];
    const items: MyRequestListItem[] = rows.map((row) => {
      const o = (row || {}) as Record<string, unknown>;
      const createdOn = pickDate(o.createdOn, o.CreatedOn, o.createdAtUtc);
      const closedOn = pickDate(o.closedOn, o.ClosedOn);
      return {
        ticketId: (o.ticketId ?? o.TicketId ?? o.id ?? o.Id) as number | string,
        ticketNumber: (o.ticketNumber ?? o.TicketNumber) as string | undefined,
        title: (o.title ?? o.Title) as string | undefined,
        status: (o.status ?? o.Status) as string | undefined,
        priority: (o.priority ?? o.Priority) as number | string | undefined,
        categoryName: (o.categoryName ?? o.CategoryName) as string | undefined,
        createdOn,
        closedOn,
        updatedOn: closedOn || createdOn
      };
    });
    return { ok: true, items };
  } catch {
    return {
      ok: false,
      error: createSnapError(
        'transport_failed',
        SnapDenyCodes.TransportFailed,
        CustomerMessages.myRequestsUnavailable,
        { correlationId: args.correlationId, retryable: true, userSafe: true }
      )
    };
  }
}

export async function getRequestDetail(args: {
  apiBaseUrl: string;
  endpoints: SnapEndpointDiscovery;
  authorization: string;
  correlationId: string;
  ticketId: number | string;
  fetchImpl: typeof fetch;
  signal?: AbortSignal;
}): Promise<{ ok: true; detail: MyRequestDetail } | { ok: false; error: SnapError }> {
  const template =
    args.endpoints.requestDetailPathTemplate || '/api/v1/SupportTickets/GetDetails/{id}';
  const url = joinUrl(
    args.apiBaseUrl,
    `${replaceTicketPath(template, args.ticketId)}?asCustomer=true`
  );
  try {
    const response = await args.fetchImpl(url, {
      method: 'GET',
      headers: authHeaders(args.endpoints, args.authorization, args.correlationId),
      credentials: 'omit',
      signal: args.signal
    });
    let body: Record<string, unknown> = {};
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch {
      body = {};
    }
    if (!response.ok) {
      return {
        ok: false,
        error: mapHttpError(
          body,
          response.status,
          args.correlationId,
          CustomerMessages.myRequestsUnavailable
        )
      };
    }
    const commentsRaw = (body.comments ?? body.Comments ?? []) as unknown[];
    const attachmentsRaw = (body.attachments ?? body.Attachments ?? []) as unknown[];
    const activityRaw = (body.activity ?? body.Activity ?? body.timeline ?? []) as unknown[];
    const createdOn = pickDate(body.createdOn, body.CreatedOn);
    const closedOn = pickDate(body.closedOn, body.ClosedOn);
    return {
      ok: true,
      detail: {
        ticketId: (body.ticketId ?? body.TicketId ?? args.ticketId) as number | string,
        ticketNumber: (body.ticketNumber ?? body.TicketNumber) as string | undefined,
        title: (body.title ?? body.Title) as string | undefined,
        description: (body.description ?? body.Description) as string | undefined,
        status: (body.status ?? body.Status) as string | undefined,
        priority: (body.priority ?? body.Priority) as number | string | undefined,
        categoryName: (body.categoryName ?? body.CategoryName) as string | undefined,
        createdOn,
        closedOn,
        comments: commentsRaw.map((c) => {
          const o = (c || {}) as Record<string, unknown>;
          return {
            commentId: (o.commentId ?? o.CommentId) as number | string | undefined,
            comment: (o.comment ?? o.Comment) as string | undefined,
            createdOn: pickDate(o.createdOn, o.CreatedOn),
            author: (o.authorDisplayName ??
              o.AuthorDisplayName ??
              o.authorName ??
              o.AuthorName ??
              o.createdByName ??
              o.CreatedByName) as string | undefined
          };
        }),
        attachments: attachmentsRaw.map((a) => {
          const o = (a || {}) as Record<string, unknown>;
          return {
            id: (o.id ?? o.Id) as number | string | undefined,
            fileName: (o.fileName ?? o.FileName) as string | undefined,
            contentType: (o.contentType ?? o.ContentType) as string | undefined,
            length: (o.length ?? o.Length) as number | undefined,
            evidenceKind: (o.evidenceKind ?? o.EvidenceKind) as string | undefined,
            sourceProducer: (o.sourceProducer ?? o.SourceProducer) as string | undefined,
            uploadedOn: pickDate(o.uploadedOn, o.UploadedOn)
          };
        }),
        activity: activityRaw.map((e) => {
          const o = (e || {}) as Record<string, unknown>;
          return {
            summary: (o.summary ?? o.Summary ?? o.description ?? o.Description ?? o.eventType) as
              | string
              | undefined,
            createdOn: pickDate(o.createdOn, o.CreatedOn, o.atUtc),
            actor: (o.actor ?? o.Actor ?? o.actorName ?? o.ActorName) as string | undefined
          };
        })
      }
    };
  } catch {
    return {
      ok: false,
      error: createSnapError(
        'transport_failed',
        SnapDenyCodes.TransportFailed,
        CustomerMessages.myRequestsUnavailable,
        { correlationId: args.correlationId, retryable: true, userSafe: true }
      )
    };
  }
}

export async function addRequestComment(args: {
  apiBaseUrl: string;
  endpoints: SnapEndpointDiscovery;
  authorization: string;
  correlationId: string;
  ticketId: number | string;
  comment: string;
  fetchImpl: typeof fetch;
  signal?: AbortSignal;
}): Promise<{ ok: true } | { ok: false; error: SnapError }> {
  const path = args.endpoints.commentPath || '/api/v1/SupportTickets/comment';
  const url = joinUrl(args.apiBaseUrl, path);
  try {
    const response = await args.fetchImpl(url, {
      method: 'POST',
      headers: {
        ...authHeaders(args.endpoints, args.authorization, args.correlationId),
        'Content-Type': 'application/json'
      },
      credentials: 'omit',
      body: JSON.stringify({
        ticketId: args.ticketId,
        comment: args.comment,
        commentType: 'Public'
      }),
      signal: args.signal
    });
    if (!response.ok) {
      let body: Record<string, unknown> = {};
      try {
        body = (await response.json()) as Record<string, unknown>;
      } catch {
        body = {};
      }
      return {
        ok: false,
        error: mapHttpError(
          body,
          response.status,
          args.correlationId,
          CustomerMessages.submissionFailed
        )
      };
    }
    return { ok: true };
  } catch {
    return {
      ok: false,
      error: createSnapError(
        'transport_failed',
        SnapDenyCodes.TransportFailed,
        CustomerMessages.submissionFailed,
        { correlationId: args.correlationId, retryable: true, userSafe: true }
      )
    };
  }
}

/**
 * Soft poll helper — ticket conversation is a projection of SupportTicket comments,
 * not a parallel chat store. Callers own interval lifecycle.
 */
export function createConversationPoller(args: {
  intervalMs?: number;
  refresh: () => void | Promise<void>;
}): { start: () => void; stop: () => void; isRunning: () => boolean } {
  const intervalMs = Math.max(4000, args.intervalMs ?? 8000);
  let timer: ReturnType<typeof setInterval> | null = null;
  return {
    start() {
      if (timer) return;
      timer = setInterval(() => {
        void args.refresh();
      }, intervalMs);
    },
    stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    },
    isRunning() {
      return timer != null;
    }
  };
}
